import api from "@/lib/api";
import { Team, CreateTeamData, UpdateTeamData } from "@/types/team";
import { Game } from "@/types/game";

export type MemberReceivedRatingEntry = {
  id: string;
  stars: number;
  createdAt: string;
  rater: {
    userId: string;
    name: string;
    avatar: string | null;
  };
  game: {
    id: string;
    name: string;
    date: string;
  };
};

export type MemberReceivedRatingsPayload = {
  member: {
    id: string;
    userId: string;
    name: string;
    avatar: string | null;
  };
  summary: {
    ratingsCount: number;
    averageStars: number | null;
    effectiveSkill: number | null;
    skillStars: number | null;
  };
  entries: MemberReceivedRatingEntry[];
};

export type GamificationRankedMember = {
  rank: number;
  userId: string;
  name: string;
  avatar?: string | null;
  gamesParticipated: number;
  presenceRate: number;
  averageRating: number | null;
  ratingsReceivedCount: number;
  ratingsGivenCount: number;
  confirmationSpeedPoints: number;
  checkInPoints: number;
  tournamentsParticipated: number;
  combinedScore: number;
};

export type RankingPrizePlaceCalculation = {
  place: 1 | 2 | 3;
  sharePercent: number;
  grossAmount: number;
  cap: number;
  finalAmount: number;
  accountedAmount?: number;
  projectedAmount?: number;
  userId?: string;
  name?: string;
  avatar?: string | null;
};

export type RankingPrizePreview = {
  enabled: boolean;
  eligible: boolean;
  ineligibleReason?: string;
  monthlyNetProfit: number;
  projectedNetProfit?: number;
  forecastNetProfit?: number;
  accountedFundTotal?: number;
  projectedFundTotal?: number;
  cashShare: number;
  prizePool: number;
  plrPool: number;
  fundTotal: number;
  placeCalculations: RankingPrizePlaceCalculation[];
  places: Array<{
    place: 1 | 2 | 3;
    userId: string;
    name: string;
    amount: number;
    avatar?: string | null;
  }>;
  cashBalance: number;
  minCashRequired: number;
  alreadySettled: boolean;
};

export type TeamMemberGamificationRanking = {
  currentMonth: {
    monthKey: string;
    label: string;
    totalGames: number;
    rankings: {
      combined: GamificationRankedMember[];
      games: GamificationRankedMember[];
      presence: GamificationRankedMember[];
      ratings: GamificationRankedMember[];
    };
    myRanks: {
      combined: number | null;
      games: number | null;
      presence: number | null;
      ratings: number | null;
    };
    prizePreview: RankingPrizePreview | null;
  };
  pastChampions: Array<{
    monthKey: string;
    label: string;
    totalGames: number;
    champion: GamificationRankedMember | null;
    secondPlace: GamificationRankedMember | null;
    thirdPlace: GamificationRankedMember | null;
    prizePreview: {
      enabled: boolean;
      fundTotal: number;
      alreadySettled: boolean;
      places: Array<{
        place: 1 | 2 | 3;
        userId: string;
        name: string;
        amount: number;
      }>;
    } | null;
    categoryWinners: {
      games: GamificationRankedMember | null;
      presence: GamificationRankedMember | null;
      ratings: GamificationRankedMember | null;
    };
  }>;
};

export type MemberGamificationGameBreakdownEntry = {
  gameId: string;
  gameName: string;
  gameDate: string;
  gameEndDate: string | null;
  participationPoints: number;
  confirmationSpeedPoints: number;
  confirmedAt: string;
  checkInPoints: number;
  checkedInAt: string | null;
  ratingsGivenCount: number;
  ratingsGivenPoints: number;
  gameTotalPoints: number;
};

export type MemberGamificationTournamentBreakdownEntry = {
  tournamentId: string;
  name: string;
  startsAt: string;
  points: number;
};

export type MemberGamificationGameBreakdown = {
  monthKey: string;
  label: string;
  totalGamesInMonth: number;
  summary: {
    combinedScore: number;
    gamesParticipated: number;
    presenceRate: number;
    presencePoints: number;
    ratingsGivenCount: number;
    ratingsGivenPoints: number;
    tournamentsParticipated: number;
    tournamentPoints: number;
    confirmationSpeedPoints: number;
    checkInPoints: number;
  } | null;
  games: MemberGamificationGameBreakdownEntry[];
  tournaments: MemberGamificationTournamentBreakdownEntry[];
};

export type GameRankingParticipantBreakdown = {
  userId: string;
  name: string;
  avatar: string | null;
  participationPoints: number;
  confirmationSpeedPoints: number;
  confirmedAt: string;
  checkInPoints: number;
  checkedInAt: string | null;
  ratingsGivenCount: number;
  ratingsGivenPoints: number;
  gameTotalPoints: number;
};

export type GameRankingBreakdown = {
  gameId: string;
  gameName: string;
  gameDate: string;
  gameEndDate: string | null;
  participants: GameRankingParticipantBreakdown[];
};

export type SquadBalanceStrategyOption = "AUTO" | "STRONG_WEAK_SPLIT" | "GENDER_SPLIT";

export type SquadBalanceInsights = {
  confirmedCount: number;
  squadCount: number;
  strongPool: number;
  strongTarget: number;
  distribution: {
    semClassificacao: number;
    s1: number;
    s2: number;
    s3: number;
    s4: number;
    s5: number;
  };
  memberIdByUserId: Record<string, string>;
  missingStarsUserIds: string[];
  missingGenderUserIds: string[];
  missingGenderNames: string[];
  maleCount: number;
  femaleCount: number;
  genderSplitEligible: boolean;
  preferredPositionByUserId: Record<string, string | null>;
  preferredPositionCount: number;
  coPlayCountByPairKey: Record<string, number>;
};

export type BalancedGameSquadsResult = {
  gameId: string;
  gameName: string;
  playersPerTeam: number;
  squadCount: number;
  participantCount: number;
  defaultSkillIfNoStars: number;
  balanceStrategy?: SquadBalanceStrategyOption;
  balanceAlgorithmApplied?: "SNAKE_SKILL" | "ADVANCED_FIRST" | "STRONG_WEAK_SPLIT" | "GENDER_SPLIT";
  respectPositions?: boolean;
  positionConflictCount?: number;
  squads: Array<{
    index: number;
    totalSkill: number;
    maleCount: number;
    femaleCount: number;
    unknownCount: number;
    members: Array<{
      userId: string;
      name: string;
      avatar?: string | null;
      effectiveSkill: number;
      skillStars?: number | null;
      averageRating?: number | null;
      ratingsCount?: number;
      gender: string;
      preferredPosition?: string | null;
    }>;
  }>;
};

export class TeamService {
  async create(data: CreateTeamData): Promise<Team> {
    const formData = new FormData();

    formData.append("name", data.name);

    if (data.slug !== undefined && data.slug.trim() !== "") {
      formData.append("slug", data.slug.trim());
    }

    if (data.photo) {
      formData.append("photo", data.photo);
    }
    if (data.coverPhoto) {
      formData.append("coverPhoto", data.coverPhoto);
    }

    if (data.instagram) {
      formData.append("instagram", data.instagram);
    }

    if (data.description) {
      formData.append("description", data.description);
    }

    if (data.city) {
      formData.append("city", data.city);
    }

    if (data.whatsappLink) {
      formData.append("whatsappLink", data.whatsappLink);
    }

    formData.append("isPublic", String(data.isPublic ?? true));

    const response = await api.post<Team>("/teams", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  async findAll(): Promise<Team[]> {
    const response = await api.get<Team[]>("/teams");
    return response.data;
  }

  async findOne(id: string): Promise<Team> {
    const response = await api.get<Team>(`/teams/${id}`);
    return response.data;
  }

  async getGames(teamId: string): Promise<Game[]> {
    const response = await api.get<Game[]>(`/teams/${teamId}/games`);
    return response.data;
  }

  async update(id: string, data: UpdateTeamData): Promise<Team> {
    const formData = new FormData();

    if (data.name !== undefined) {
      formData.append("name", data.name);
    }

    if (data.slug !== undefined && data.slug.trim() !== "") {
      formData.append("slug", data.slug.trim());
    }

    if (data.photo) {
      formData.append("photo", data.photo);
    }
    if (data.coverPhoto) {
      formData.append("coverPhoto", data.coverPhoto);
    }

    if (data.instagram !== undefined) {
      formData.append("instagram", data.instagram || "");
    }

    if (data.description !== undefined) {
      formData.append("description", data.description || "");
    }

    if (data.city !== undefined) {
      formData.append("city", data.city || "");
    }

    if (data.whatsappLink !== undefined) {
      formData.append("whatsappLink", data.whatsappLink || "");
    }

    if (data.isPublic !== undefined) {
      formData.append("isPublic", String(data.isPublic));
    }

    const response = await api.put<Team>(`/teams/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/teams/${id}`);
  }

  async addMember(id: string): Promise<void> {
    await api.post(`/teams/${id}/members`);
  }

  async getMembers(id: string): Promise<{
    members: Array<{
      id: string;
      userId: string;
      name: string;
      email?: string;
      whatsapp?: string | null;
      phone?: string;
      avatar?: string;
      profileCover?: string | null;
      gender?: string | null;
      role: string;
      vipSource?: "MANUAL" | "SUBSCRIPTION" | null;
      status: string;
      skillStars?: number | null;
      joinedAt: string;
    }>;
    total: number;
  }> {
    const response = await api.get(`/teams/${id}/members`);
    return response.data;
  }

  async getMemberPreview(id: string): Promise<{
    members: Array<{
      id: string;
      userId: string;
      name: string;
      avatar?: string;
      profileCover?: string | null;
      role: string;
      status: string;
      gamesParticipated: number;
    }>;
    total: number;
  }> {
    const response = await api.get(`/teams/${id}/member-preview`);
    return response.data;
  }

  async getRecentActivities(
    id: string,
    limit = 10,
  ): Promise<
    Array<{
      id: string;
      type: "registration" | "payment" | "check-in" | "rating";
      userName: string;
      userAvatar?: string | null;
      gameName: string;
      gameId: string;
      timestamp: string;
    }>
  > {
    const response = await api.get(`/teams/${id}/recent-activities`, {
      params: { limit },
    });
    return response.data;
  }

  async getFeedbackGameOptions(id: string): Promise<
    Array<{ id: string; name: string; gameDate: string }>
  > {
    const response = await api.get(`/teams/${id}/feedbacks/games`);
    return response.data;
  }

  async getGameFeedbacks(
    id: string,
    params?: {
      page?: number;
      limit?: number;
      gameId?: string;
      stars?: number;
      visibility?: 'public' | 'private';
      q?: string;
      hasComment?: boolean;
    },
  ): Promise<{
    items: Array<{
      id: string;
      gameId: string;
      gameName: string;
      gameDate: string;
      userId: string;
      userName: string;
      userAvatar: string | null;
      stars: number;
      comment: string | null;
      isPublic: boolean;
      createdAt: string;
      updatedAt: string;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const response = await api.get(`/teams/${id}/feedbacks`, {
      params: {
        page: params?.page,
        limit: params?.limit,
        gameId: params?.gameId,
        stars: params?.stars,
        visibility: params?.visibility,
        q: params?.q,
        hasComment: params?.hasComment,
      },
    });
    return response.data;
  }

  async getMembersWithStats(id: string): Promise<{
    members: Array<{
      id: string;
      userId: string;
      name: string;
      email?: string;
      whatsapp?: string | null;
      phone?: string;
      avatar?: string;
      profileCover?: string | null;
      gender?: string | null;
      role: string;
      vipSource?: "MANUAL" | "SUBSCRIPTION" | null;
      status: string;
      skillStars?: number | null;
      joinedAt: string;
      gamesParticipated: number;
      lastParticipation: string | null;
      effectiveSkill?: number | null;
      recentGames: Array<{
        id: string;
        name: string;
        date: string;
      }>;
      averageRating: number | null;
      ratingsReceivedCount: number;
      ranking: number;
    }>;
    total: number;
  }> {
    const response = await api.get(`/teams/${id}/members-with-stats`);
    return response.data;
  }

  async getMemberDirectory(id: string): Promise<{
    members: Array<{
      id: string;
      userId: string;
      name: string;
      email?: string;
      whatsapp?: string | null;
      phone?: string;
      avatar?: string;
      profileCover?: string | null;
      gender?: string | null;
      role: string;
      vipSource?: "MANUAL" | "SUBSCRIPTION" | null;
      status: string;
      skillStars?: number | null;
      joinedAt: string;
      gamesParticipated: number;
      lastParticipation: string | null;
      effectiveSkill?: number | null;
      recentGames: Array<{
        id: string;
        name: string;
        date: string;
      }>;
      averageRating: number | null;
      ratingsReceivedCount: number;
      ranking: number;
    }>;
    total: number;
  }> {
    const response = await api.get(`/teams/${id}/member-directory`);
    return response.data;
  }

  async getMemberGamificationRanking(
    id: string,
    monthKey?: string,
  ): Promise<TeamMemberGamificationRanking> {
    const response = await api.get(`/teams/${id}/member-ranking`, {
      params: monthKey ? { monthKey } : undefined,
    });
    return response.data;
  }

  async getMyMemberGamificationGameBreakdown(
    id: string,
    monthKey?: string,
  ): Promise<MemberGamificationGameBreakdown> {
    const response = await api.get(`/teams/${id}/member-ranking/me/breakdown`, {
      params: monthKey ? { monthKey } : undefined,
    });
    return response.data;
  }

  async getMemberGamificationGameBreakdown(
    id: string,
    memberUserId: string,
    monthKey?: string,
  ): Promise<MemberGamificationGameBreakdown> {
    const response = await api.get(
      `/teams/${id}/member-ranking/members/${memberUserId}/breakdown`,
      {
        params: monthKey ? { monthKey } : undefined,
      },
    );
    return response.data;
  }

  async getGameRankingBreakdown(
    teamId: string,
    gameId: string,
  ): Promise<GameRankingBreakdown> {
    const response = await api.get<GameRankingBreakdown>(
      `/teams/${teamId}/games/${gameId}/ranking-breakdown`,
    );
    return response.data;
  }

  async getMemberConnections(id: string): Promise<{
    basedOn: "SQUADS" | "GAMES";
    totalGames: number;
    nodes: Array<{
      userId: string;
      name: string;
      avatar: string | null;
      role: string;
      gamesParticipated: number;
    }>;
    links: Array<{
      source: string;
      target: string;
      weight: number;
    }>;
  }> {
    const response = await api.get(`/teams/${id}/member-connections`);
    return response.data;
  }

  async getMyRole(teamId: string): Promise<string> {
    const response = await api.get<{ role: string }>(
      `/teams/${teamId}/members/me`,
    );
    return response.data.role;
  }

  async updateMemberRole(teamId: string, memberId: string, role: string) {
    const response = await api.patch(
      `/teams/${teamId}/members/${memberId}/role`,
      { role },
    );
    return response.data;
  }

  async updateMemberSkillStars(
    teamId: string,
    memberId: string,
    stars: number | null,
  ): Promise<{ id: string; userId: string; skillStars: number | null }> {
    const response = await api.patch(
      `/teams/${teamId}/members/${memberId}/skill-stars`,
      { stars },
    );
    return response.data;
  }

  async updateMemberGender(
    teamId: string,
    memberId: string,
    gender: string | null,
  ): Promise<{ memberId: string; userId: string; gender: string | null }> {
    const response = await api.patch(
      `/teams/${teamId}/members/${memberId}/gender`,
      { gender },
    );
    return response.data;
  }

  async updateGameParticipantPreferredPosition(
    teamId: string,
    gameId: string,
    participantUserId: string,
    preferredPosition: string | null,
  ): Promise<{ userId: string; preferredPosition: string | null }> {
    const response = await api.patch(
      `/teams/${teamId}/games/${gameId}/participants/${participantUserId}/preferred-position`,
      { preferredPosition },
    );
    return response.data;
  }

  async deactivateMember(teamId: string, memberId: string) {
    const response = await api.patch(
      `/teams/${teamId}/members/${memberId}/deactivate`,
    );
    return response.data;
  }

  async reactivateMember(teamId: string, memberId: string) {
    const response = await api.patch(
      `/teams/${teamId}/members/${memberId}/reactivate`,
    );
    return response.data;
  }

  async getMemberReceivedRatings(teamId: string, memberId: string) {
    const response = await api.get<MemberReceivedRatingsPayload>(
      `/teams/${teamId}/members/${memberId}/received-ratings`,
    );
    return response.data;
  }

  async addGalleryPhoto(teamId: string, file: File): Promise<Team> {
    const formData = new FormData();
    formData.append("photo", file);
    const response = await api.post<Team>(`/teams/${teamId}/gallery-photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async removeGalleryPhoto(teamId: string, photoIndex: number): Promise<Team> {
    const response = await api.delete<Team>(`/teams/${teamId}/gallery-photos/${photoIndex}`);
    return response.data;
  }

  async balanceGameSquads(
    teamId: string,
    gameId: string,
    playersPerTeam: number,
    strategy: SquadBalanceStrategyOption = "AUTO",
    respectPositions = true,
  ): Promise<BalancedGameSquadsResult> {
    const response = await api.post(
      `/teams/${teamId}/games/${gameId}/balance-squads`,
      { playersPerTeam, strategy, respectPositions },
    );
    return response.data;
  }

  async getPersistedBalancedGameSquads(
    teamId: string,
    gameId: string,
  ): Promise<BalancedGameSquadsResult | null> {
    const response = await api.get(`/teams/${teamId}/games/${gameId}/balance-squads`);
    return response.data;
  }

  async getSquadBalanceInsights(
    teamId: string,
    gameId: string,
    playersPerTeam: number,
  ): Promise<SquadBalanceInsights> {
    const response = await api.get<SquadBalanceInsights>(
      `/teams/${teamId}/games/${gameId}/squad-balance-insights`,
      { params: { playersPerTeam } },
    );
    return response.data;
  }

  async confirmBalancedGameSquads(
    teamId: string,
    gameId: string,
    playersPerTeam: number,
    squads?: Array<{ userIds: string[] }>,
    strategy?: SquadBalanceStrategyOption,
    respectPositions?: boolean,
  ) {
    const body: {
      playersPerTeam: number;
      squads?: Array<{ userIds: string[] }>;
      strategy?: SquadBalanceStrategyOption;
      respectPositions?: boolean;
    } = {
      playersPerTeam,
    };
    if (squads !== undefined) {
      body.squads = squads;
    }
    if (strategy !== undefined) {
      body.strategy = strategy;
    }
    if (respectPositions !== undefined) {
      body.respectPositions = respectPositions;
    }
    const response = await api.post(
      `/teams/${teamId}/games/${gameId}/balance-squads/confirm`,
      body,
    );
    return response.data;
  }

  async clearBalancedGameSquads(teamId: string, gameId: string): Promise<{ cleared: boolean }> {
    const response = await api.delete<{ cleared: boolean }>(
      `/teams/${teamId}/games/${gameId}/balance-squads`,
    );
    return response.data;
  }

  async removeMember(teamId: string, memberId: string) {
    const response = await api.delete(`/teams/${teamId}/members/${memberId}`);
    return response.data;
  }
}

export const teamService = new TeamService();
